import React, {
	createContext,
	useContext,
	useCallback,
	useDeferredValue,
	useEffect,
	useMemo,
	useState,
	useSyncExternalStore,
	type ReactNode,
} from 'react';
import { usePreloadedQuery, useRelayEnvironment } from 'react-relay';

import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { RelaySuspense } from '@confluence/query-preloader-tools/entry-points/RelaySuspense.ts';
import { HydrationSafeComponent } from '@confluence/hydration-utils/entry-points/HydrationSafeComponent.tsx';

import { createOperationDescriptor, getRequest } from 'relay-runtime';

import {
	getPersonalizationQueryOwner,
	subscribePersonalizationQueryOwner,
	type PersonalizationReaderReference,
} from './PersonalizationQueryOwner.ts';

import { ViewPagePersonalizationQuery } from './ViewPagePersonalizationQuery.graphql.ts';
import type { ViewPagePersonalizationQuery as PersonalizationQuery } from './__generated__/ViewPagePersonalizationQuery.graphql.ts';

type Props = {
	variables: PersonalizationQuery['variables'];
	children: (data: PersonalizationQuery['response']) => ReactNode;
	fallback: ReactNode;
};

// GenericErrorBoundary retains its initial renderOnError component. Context ensures recovery
// always uses the current route's fallback, including after content or space changes.
const FallbackContext = createContext<ReactNode>(null);
const CurrentFallback = (): React.JSX.Element => <>{useContext(FallbackContext)}</>;

const AvailableReader = ({
	reference,
	children,
}: {
	reference: PersonalizationReaderReference;
	children: Props['children'];
}): React.JSX.Element => {
	const data = usePreloadedQuery<PersonalizationQuery>(
		ViewPagePersonalizationQuery,
		reference.reference,
	);
	return <>{children(data)}</>;
};
const Reader = ({ variables, children, fallback }: Props): React.JSX.Element => {
	const environment = useRelayEnvironment();
	const subscribe = useCallback(
		(listener: () => void) => subscribePersonalizationQueryOwner(environment, listener),
		[environment],
	);
	const snapshot = useCallback(
		() => getPersonalizationQueryOwner(environment)?.get(variables),
		[environment, variables],
	);
	const requested = useSyncExternalStore(subscribe, snapshot, snapshot);
	const deferred = useDeferredValue(requested);
	// Defer same-route replacements, never show an old route's result under a new identity.
	const deferredMatchesVariables = useMemo(
		() => deferred && JSON.stringify(deferred.variables) === JSON.stringify(variables),
		[deferred, variables],
	);
	const reference = deferredMatchesVariables ? deferred : requested;
	useEffect(
		() => requested && getPersonalizationQueryOwner(environment)?.retain(requested),
		[environment, requested],
	);
	useEffect(
		() => reference && getPersonalizationQueryOwner(environment)?.retain(reference),
		[environment, reference],
	);
	const operation = useMemo(
		() => createOperationDescriptor(getRequest(ViewPagePersonalizationQuery), variables),
		[variables],
	);
	return reference && !reference.disposed && environment.check(operation).status === 'available' ? (
		<AvailableReader reference={reference}>{children}</AvailableReader>
	) : (
		<>{fallback}</>
	);
};

/**
 * The route owns the query references; this reader acquires and consumes its selected group.
 * Consumers select only their group and pass its genuine masked refs to useFragment. This reader
 * never starts a network request. Missing selections must use the caller's legacy fallback.
 */
const PersonalizationReaderBoundary = ({
	variables,
	children,
	fallback,
}: Props): React.JSX.Element => {
	const environment = useRelayEnvironment();
	const identity = useMemo(() => JSON.stringify(variables), [variables]);
	const [boundary, setBoundary] = useState<{
		key: number;
		failedOwner?: { identity: string; environment: typeof environment };
	}>({ key: 0 });
	if (
		boundary.failedOwner &&
		(boundary.failedOwner.identity !== identity || boundary.failedOwner.environment !== environment)
	) {
		// Retry only a failed boundary. Healthy navigation must preserve consumer state.
		setBoundary({ key: boundary.key + 1 });
	}
	return (
		<FallbackContext.Provider value={fallback}>
			<GenericErrorBoundary
				key={boundary.key}
				attribution={Attribution.INHERIT}
				renderOnError={CurrentFallback}
				onError={() => setBoundary({ key: boundary.key, failedOwner: { identity, environment } })}
			>
				<RelaySuspense fallback={fallback} interactionName="ViewPagePersonalization">
					<Reader variables={variables} fallback={fallback}>
						{children}
					</Reader>
				</RelaySuspense>
			</GenericErrorBoundary>
		</FallbackContext.Provider>
	);
};

export const ViewPagePersonalizationReader = (props: Props): React.JSX.Element => (
	// Parent header updates must not replace the boundary's props before its server HTML hydrates.
	// Keep the existing Relay/UFO boundary and apply the latest props through the shared transition.
	<HydrationSafeComponent>
		<PersonalizationReaderBoundary {...props} />
	</HydrationSafeComponent>
);
